import React, { memo } from "react";
import * as PropTypes from "prop-types";
import { databasePropType } from "../../../propTypes";
import AsynchronousDataWidget from "../../widgets/AsynchronousDataWidget";
import MethodMixWithinSectorChartWidget from "./MethodMixWithinSectorChartWidget";
import ShareOfUsersChartWidget from "./ShareOfUsersChartWidget";
import SmallFigure from "../layout/SmallFigure";
import ThemeIntroductionController from "../layout/ThemeIntroductionController";
import UsersByMethodChartWidget from "./UsersByMethodChartWidget";
import UsersBySectorTableWidget from "./UsersBySectorTableWidget";
import TextOnly from "../layout/TextOnly";
import MultiFigure from "../layout/MultiFigure";

const Theme2 = props => {
  const database = props.database;
  const data = props.data;

  return (
    <section id="theme2" className="theme">
      <ThemeIntroductionController
        reference="02"
        title="Distinct Landscapes Between the Public and Private&nbsp;Sectors"
      >
        While the public and private sectors each play important roles in the
        contraceptive supplies market, the two are not interchangeable. The
        public sector primarily provides long-acting and permanent methods
        (LAPMs), while the private sector focuses more on short-term methods
        (STMs). This means that in the context of shifting funding landscapes,
        the private sector cannot compensate for a lack of funding in the public
        sector. In response to shrinking donor funding, care must be taken to
        ensure women can make their own choices from a full range of
        contraceptive methods.
      </ThemeIntroductionController>

      <TextOnly>
        <p>
          Currently, there are 467 million users of contraception living in the
          countries that constitute the low- and middle-income countries market
          for supplies. Overall, these users are almost equally split between
          LAPMs and STMs with 232 million and 235 million users, respectively.
          The predominant method of contraception in LMICs is sterilization; its
          162 million users make up more than one-third of the total method mix.
          One in five users of contraception (91.8 million) consume pills,
          making it the second most popular contraceptive method in the LMIC
          market.
        </p>
      </TextOnly>

      {/*Figure 2.1*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <UsersByMethodChartWidget
              database={database}
              data={data ? data.usersByMethodChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          When comparing the differences in method use between the public and
          private sectors, one can observe a division of labor of sorts, where
          the public sector plays a greater role in delivering LAPMs, while
          private sector entities provide the majority of STMs. Injectables
          effectively straddle the divide: slightly more injectable users get
          their method from the public sector (37.4 million) than the private
          sector (32 million). Of the total number of users of contraception in
          LMICs, 57% (268 million) obtain supplies from the public sector while
          the remaining 43% (199 million) obtain their supplies from private
          sector entities.{" "}
        </p>
        <p>
          Within the public sector, two-thirds (67%) of users rely on LAPMs. By
          contrast, users of pills, injectables, condoms, and other modern
          methods&mdash;all STMs&mdash;make up the majority (73%) of the private
          sector method mix. This pattern seems to indicate that investments in
          healthcare facilities and staff training, combined with the potential
          to leverage economies of scale, give the public sector an advantage in
          providing LAPMs, which require a medical service, trained personnel to
          provide that service, and a physical space in which the service can be
          provided. STMs, especially pills and condoms, on the other hand, often
          require little more than a simple retail transaction. The dominance of
          STMs in the private sector may be a function of limits on what can be
          offered and by whom within private sector entities due to their type
          of facility (such as drug shops) or level of provider. They may also
          be a function of the greater profit-making potential of methods that
          require re-supply.
        </p>
      </SmallFigure>

      <h3 className="section-heading">
        Public versus Private Sectors within GNI Groups
      </h3>

      <MultiFigure
        largeFigure={
          // Table 2.1
          <AsynchronousDataWidget loaded={!!(data && data.usersBySectorTable)}>
            <UsersBySectorTableWidget
              database={database}
              data={data ? data.usersBySectorTable : []}
              onChange={props.onWidgetChange}
              onDownloadCsv={settings => {
                props.onDownloadCsv("theme2.usersBySectorTable", settings);
              }}
            />
          </AsynchronousDataWidget>
        }
        smallFigure={
          // Figure 2.2
          <AsynchronousDataWidget loaded={!!data}>
            <ShareOfUsersChartWidget
              database={database}
              data={data ? data.shareOfUsersChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
      >
        <p>
          With 283 million users, lower-middle-income countries account for 61%
          of the total number of users. Upper-middle-income countries account
          for 30% of users (139 million), while low-income countries account for
          only 10% (44.7 million).{" "}
        </p>
        <p>
          Within low-income countries the most commonly used method is
          injectables, accounting for 36% of users (16 million). Fewer than half
          that number use each of the next most popular methods: 7.13 million
          women (16% of users) use pills and 6.65 million women (15% of users)
          use implants.
        </p>
        <p>
          In lower-middle-income countries, sterilization is the predominant
          method (43%), followed by pills (18%) and injectables (14%). Because
          this GNI group represents almost two-thirds of total users, combined
          with the fact that India&mdash;a lower-middle income
          country&mdash;represents such a large share of users compared to other
          LMIC countries, the overall LMIC method mix closely mirrors that of
          lower-middle-income countries.{" "}
        </p>
        <p>
          Within upper middle-income countries, sterilization is also the most
          prevalent method, but it accounts for a much smaller share of overall
          use (27%). Pills and condoms each make up roughly comparable shares of
          the method mix (25% and 21% respectively).{" "}
        </p>
        <p>
          Lower-middle-income countries are not only home to the largest number
          of users overall, but also to the largest number of both public sector
          users (166 million) and private sector users (116 million). In
          relative terms, however, the contribution of the public sector
          diminishes as GNI increases; it is the largest share in low-income
          countries (70%), and smallest in upper-middle-income countries (50%).{" "}
        </p>
      </MultiFigure>

      {/*Figure 2.3*/}
      <SmallFigure
        figure={
          <AsynchronousDataWidget loaded={!!data}>
            <MethodMixWithinSectorChartWidget
              database={database}
              data={data ? data.methodMixWithinSectorChart : []}
              onChange={props.onWidgetChange}
            />
          </AsynchronousDataWidget>
        }
        figureOnLeft={true}
      >
        <p>
          While there are some differences in the private sector method mix
          across the three GNI groups, these differences are subtle. Between 70%
          and 75% of the private sector method mix in each income group is made
          up of just three methods: injectables, pills and condoms. The relative
          use of these three methods, however, varies. In low-income countries,
          condoms and injectables are the most common methods used by private
          sector users, while in lower- and upper-middle-income countries, pills
          are the most common method in the private sector.{" "}
        </p>
        <p>
          Within the public sector, however, the differences in method mix
          across GNI groups becomes more pronounced. Injectables account for 40%
          of the public sector method mix in low-income countries, but only 10%
          and 12% in lower- and upper-middle-income countries, respectively. We
          also see a much more pronounced role of implants within public sector
          users in low-income countries&mdash;19% compared to less than 5% in
          the other two GNI groups. In fact, despite many fewer public sector
          users in low-income countries compared to the other two GNI groups, in
          absolute terms there are more public sector implant users (5.88
          million) living in low-income countries than in lower- or
          upper-middle-income countries (4.97 million and 2.5 million,
          respectively).
        </p>
        <p>
          A more significant difference emerges in the role of public sector
          sterilization. Within lower-middle-income countries nearly 60% of
          public sector users rely on sterilization compared to only 9% in
          low-income countries and 39% in upper middle-income countries.
        </p>
        <p>
          The differences between the method mix in the public and private
          sectors suggest that shifting efforts from the public to the private
          sector as a strategy to deal with limited donor funding may not be
          feasible on its own. Along with cost barriers, such a shift may limit
          method choice in situations where private sector entities play a
          limited role in providing LAPMs.
        </p>
      </SmallFigure>
    </section>
  );
};

Theme2.propTypes = {
  database: databasePropType,
  data: PropTypes.object,
  onWidgetChange: PropTypes.func.isRequired,
  onDownloadCsv: PropTypes.func.isRequired
};

export default memo(Theme2);
